import { FC, useState, ChangeEvent, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import lRut from 'rut.js'

import CONFIG from '../../../config'
import { IAuthLoginForm, IAuthLogin, EDocumentType, FormResponseStatus, AuthRoles } from '../../models'
import { Button, Input, Link, Loading, Radio, Text } from '../UI'
import { authActions, profileActions } from '../../store/actions'
import { useUserDocumentsTypes } from '../../hooks'
import { ShowPassword } from '../UI/Input/ShowPassword'

type LoginProps = {
  className?: string,
  role?: AuthRoles,
  onSubmit(status: FormResponseStatus): void
}

const defaultValues: IAuthLoginForm = {
  documentTypeId: String(EDocumentType.rut),
  documentNumber: '',
  password: ''
}

const Login:FC<LoginProps> = ({ className, role = '', onSubmit,...rest })  => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { register, handleSubmit, formState: { errors }, clearErrors, watch, setValue } = useForm<IAuthLoginForm>({
    defaultValues
  })
  const [submitStoreResp, setSubmitStoreResp] = useState<boolean>(false)
  const [submited, setSubmited] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState(false);
  const docType: number = Number(watch('documentTypeId'))
  const documentsTypes = useUserDocumentsTypes()

  const logging = useSelector((state: any) => state.login)

  if(submitStoreResp && logging && JSON.stringify(logging) !== '{}') {
    if(logging.type === 'SUCCESS') {
      const { patient: { patient } } = logging

      setSubmitStoreResp(false)

      if(patient && patient.temporary_key_status && role=='') {
        dispatch(profileActions.changePasswordProfileSw(true))
        history.push(CONFIG.ROUTES.DASHBOARD.PROFILE)

      } else {
        setTimeout(() => onSubmit('SUCCESS'), 800)
      }
    } else if(logging.type === 'ERROR') {
      setSubmitStoreResp(false)

      if(logging.error?.code === 1) {
        setSubmited(false)
        setTimeout(() => onSubmit('ERROR_YOUNGER'), 800)

      } else if(logging.error?.code === 2) {
        setSubmited(false)
        setTimeout(() => onSubmit('ERROR_NOT_REGISTERED'), 800)
      } else if(logging.error?.code === 3) {
          setSubmited(false)
          setTimeout(() => onSubmit('ERROR_LOGGED_IN_OTHER_DEVICE'), 800)
      } else {
        setTimeout(() => onSubmit('ERROR'), 800)
      }
    }
  }

  const onAuthLogin = async (auth: IAuthLoginForm) => {
    const { documentTypeId, documentNumber, password } = auth

    const data: IAuthLogin = {
      document_type_id: Number(documentTypeId),
      document_number: (Number(documentTypeId) === EDocumentType.rut ? lRut.clean(documentNumber) : documentNumber),
      password
    }

    dispatch(authActions.login(data, role))
    setSubmitStoreResp(true)
    setSubmited(true)
  }

  const onDocumentTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    setValue('documentTypeId', value)
    setValue('documentNumber', '')
    setValue('password', '')
    clearErrors()
  }

  useEffect(() => {
      if (docType === EDocumentType.rut) {
        const formattedRut = lRut.format(watch('documentNumber'), { dots: false })
        setValue('documentNumber', formattedRut)
      }
  }, [watch('documentNumber')]);
  
  
  return (
    <div className={className} {...rest}>
      <form noValidate autoComplete="off" onSubmit={handleSubmit(onAuthLogin)}>
        <fieldset>
          <Text component="label" size="14" weight="semibold" inline>Tipo de documento:</Text>

          <div className="d-inline-block position-relative align-middle ml-10">
            {documentsTypes.data ?
                documentsTypes.data.map((it: any, i: number) => {
                  const fieldId: string = `${it.name}${i}`
                  return (
                    <div key={i} className={`d-inline-block ${i > 0 ? 'ml-10' : ''}`}>
                      <Radio
                        {...register('documentTypeId', { required: 'Selecciona Tipo de documento' })}
                        id={fieldId}
                        name="documentTypeId"
                        value={it.id}
                        defaultChecked={it.id === defaultValues.documentTypeId}
                        onChange={onDocumentTypeChange}
                      />
                      <Text component="label" htmlFor={fieldId} size="14" weight="semibold">{it.name}</Text>
                    </div>
                  )
                })
              :
                <Loading size="sm" className="align-middle" />
            }
          </div>
          {errors.documentTypeId && <Text component="label" size="12" color="danger" className="d-block mt-5">{errors.documentTypeId?.message}</Text>}
        </fieldset>

        <fieldset className="mt-30">
          {docType === EDocumentType.rut &&
            <>
              <Text component="label" htmlFor="documentNumber" size="14" weight="semibold">{CONFIG.AUTH.LABEL.RUT}</Text>
              <Input
                {...register('documentNumber', {
                  required: `Digita ${CONFIG.AUTH.LABEL.RUT}`,
                  pattern: {
                    value: CONFIG.FORM.PATTERN.RUT.EXP,
                    message: `Digita ${CONFIG.AUTH.LABEL.RUT} válido`
                  },
                  validate: value => lRut.validate(value) || `Digita ${CONFIG.AUTH.LABEL.RUT} válido`
                })}
                id="documentNumber"
                name="documentNumber"
                placeholder={CONFIG.AUTH.PLACEHOLDER.RUT}
                maxLength={CONFIG.FORM.PATTERN.RUT.MAXLENGHT}
                fullWidth
                error={!!errors.documentNumber}
                className="mt-5"
              />
            </>
          }

          {docType === EDocumentType.passport &&
            <>
              <Text component="label" htmlFor="documentNumber" size="14" weight="semibold">{CONFIG.AUTH.LABEL.PASSPORT}</Text>
              <Input
                {...register('documentNumber', {
                  required: `Digita ${CONFIG.AUTH.LABEL.PASSPORT}`,
                  pattern: { value: CONFIG.FORM.PATTERN.PASSPORT.EXP, message: `Digita ${CONFIG.AUTH.LABEL.PASSPORT} válido` },
                  validate: value => true
                })}
                id="documentNumber"
                name="documentNumber"
                placeholder={CONFIG.AUTH.PLACEHOLDER.PASSPORT}
                fullWidth
                error={!!errors.documentNumber}
                className="mt-5"
              />
            </>
          }

          {errors.documentNumber && <Text component="label" size="12" color="danger" className="mt-5">{errors.documentNumber?.message}</Text>}
        </fieldset>

        <fieldset className="mt-30">
          <Text component="label" htmlFor="password" size="14" weight="semibold">Contraseña</Text>
          <Input
            {...register('password', { required: 'Digita Contraseña' })}
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            placeholder="Contraseña"
            fullWidth
            error={!!errors.password}
            className="mt-5"
          />
          {errors.password && <Text component="label" size="12" color="danger" className="mt-5">{errors.password?.message}</Text>}
          <ShowPassword
            showPassword={showPassword} 
            setShowPassword={setShowPassword} 
            inputId="password-toggle"
          />
        </fieldset>
        
        <div className="text-right mt-5">
          <Link to={role === 'ADMIN_ROLE' ? CONFIG.ROUTES.ADMIN.FORGOTPASSWORD : CONFIG.ROUTES.AUTH.FORGOTPASSWORD} size={12}>¿Has olvidado la contraseña?</Link>
        </div>

        <Button
          type="submit"
          fullWidth
          className="mt-25"
          disabled={submited}
        >INICIAR SESIÓN</Button>
      </form>
    </div>
  )
}

export { Login }